/* eslint-disable */
import type {
  Avatar,
  Button,
  HighlightText,
  ShopInfoHalfSheet,
  Text,
} from "../../p2p/profile_directory/ui/UIElements.pb";
import type { Image } from "../../ui/Image.pb";

export interface SearchCommonSection {
  row_section?: SearchCommonRowSection | undefined;
  text_row_section?: SearchCommonTextRowSection | undefined;
  placeholder_section?: SearchCommonPlaceholderSection | undefined;
  avatar_section?: SearchCommonAvatarSection | undefined;
  card_section?: SearchCommonCardSection | undefined;
  message_section?: SearchCommonMessageSection | undefined;
  default_max_items?: number;
  section_title?: Text;
  section_subtitle?: Text;
  section_action_button?: Button;
  layout?: SearchCommonSectionLayout;
  section_footer_action_button?: Button;
  tap_action?: TapAction;
}

export enum SearchCommonSectionLayout {
  ONE_ROW = "ONE_ROW",
  ONE_COLUMN = "ONE_COLUMN",
  TWO_ROWS = "TWO_ROWS",
  TWO_COLUMNS = "TWO_COLUMNS",
  THREE_ROWS = "THREE_ROWS",
  THREE_COLUMNS = "THREE_COLUMNS",
  FOUR_ROWS = "FOUR_ROWS",
  FOUR_COLUMNS = "FOUR_COLUMNS",
}

/**
 * A placeholder for sections that are not generated by cash-customer-search. Defining placeholder for them
 * in the response to provide entire screen's layout to client.
 */
export interface SearchCommonPlaceholderSection {
  identifier?: SearchCommonPlaceholderSectionIdentifier;
}

export enum SearchCommonPlaceholderSectionIdentifier {
  RECENTLY_VIEWED = "RECENTLY_VIEWED",
  RECENT_SEARCHES = "RECENT_SEARCHES",
  ZERO_SEARCH_RESULTS = "ZERO_SEARCH_RESULTS",
}

export interface SearchCommonTextRowSection {
  results?: SearchCommonTextRowItem[];
}

/** Text with icon. No avatar or image. */
export interface SearchCommonTextRowItem {
  metadata?: SearchCommonTextRowItemMetadata;
  text?: HighlightText;
  icon?: Image;
  action_url?: string;
}

/** Analytics metadata. */
export interface SearchCommonTextRowItemMetadata {
  search_text?: string;
}

export interface SearchCommonRowSection {
  results?: ProfileDirectoryRow[];
}

export interface SearchCommonAvatarSection {
  items?: SearchCommonAvatarSectionSearchCommonAvatarItem[];
}

export interface SearchCommonAvatarSectionSearchCommonAvatarItem {
  metadata?: Metadata;
  avatar?: Avatar;
  title?: Text;
}

export interface SearchCommonCardSection {
  items?: SearchCommonCardSectionSearchCommonCardItem[];
}

export interface SearchCommonCardSectionSearchCommonCardItem {
  metadata?: Metadata;
  picture?: Image;
  primary_text?: Text;
  secondary_text?: Text;
  tertiary_left_text?: Text;
  tertiary_right_text?: Text;
  item_action_url?: string;
}

/** Shows a message to the customer. Initial use case is for zero results message. */
export interface SearchCommonMessageSection {
  picture?: Image;
  primary_text?: Text;
  secondary_text?: Text;
}

/** Analytics metadata about search results. */
export interface Metadata {
  /** Opaque representation of current model used to rank results */
  model_version?: string;
  /** Full length of text field query matched on. Used for analytics. */
  match_length?: number;
  /** If false indicates search result is for non-onboarded empty customer */
  is_cash_customer?: boolean;
  /** list of fields that query matched on */
  match_fields?: string[];
  search_text?: string;
  /** Unique query identifier */
  query_token?: string;
  /** Specifies the type of the entity */
  entity_type?: MetadataEntityType;
  /** Specifies the token of the entity */
  entity_token?: string;
  /** json blob of entity attributes to be logged to snowflake */
  entity_attributes?: string;
  /** list of merchant tags from ES documents */
  merchant_tags?: string[];
  /** The name of the entity in our system for analytics purposes */
  entity_name?: string;
  /** The token of the business in our system for analytics purposes * / */
  business_token?: string;
  /** The name of the business in our system for analytics purposes * / */
  business_name?: string;
  /** Specifies the offer type of the entity */
  offer_type?: MetadataOfferType;
  /** The name of the app feature */
  app_feature_name?: string;
  /** The client route for the app feature */
  app_feature_client_route?: string;
  /** Specifies the offer id of the entity */
  offer_id?: string;
}

/**
 * Note: Any new entity type added will be handled as fatal error on mobile side,
 * So please do coordinate with mobile eng when making updates to this enum
 */
export enum MetadataEntityType {
  UNKNOWN = "UNKNOWN",
  CUSTOMER = "CUSTOMER",
  AP_STORE = "AP_STORE",
  BOOST_MERCHANT = "BOOST_MERCHANT",
  PRODUCT = "PRODUCT",
  SQ_MERCHANT = "SQ_MERCHANT",
  CAP = "CAP",
  APP_FEATURE = "APP_FEATURE",
}

export enum MetadataOfferType {
  UNKNOWN_OFFER_TYPE = "UNKNOWN_OFFER_TYPE",
  CASH_APP_PAY = "CASH_APP_PAY",
  BOOST = "BOOST",
  BNPL = "BNPL",
  SQ_LOYALTY = "SQ_LOYALTY",
  SQ_COUPON = "SQ_COUPON",
}

export interface ProfileDirectoryRow {
  /**
   * Token for the entity. Existing for backward compatibility.
   * Please stop using this and start using the entity_token from metadata.
   */
  token?: string;
  /** merchantein token for business entity */
  merchantein_token?: string;
  avatar?: Avatar;
  title?: HighlightText;
  subtitle?: HighlightText;
  /** 3rd line context */
  sub_child_title?: HighlightText;
  /** TODO: Possibly remove wtih HightlightText fields having icons */
  icon?: Image;
  /** DEPRECATED: Use `action` instead. CMKPL-364 */
  action_url?: string;
  button?: Button;
  metadata?: Metadata;
  /** Open a URL or client route */
  url?:
    | string
    | undefined;
  /** Open an info half sheet. */
  half_sheet?: ShopInfoHalfSheet | undefined;
}

export interface Filter {
  /** Price, Offers Available, or Brands */
  filter_name?: string;
  /** slider (e.g. Price) */
  price_range?:
    | PriceRange
    | undefined;
  /** on/off toggle (e.g. Offers Available) */
  toggle?:
    | boolean
    | undefined;
  /** list of checkboxes (e.g. vendors, colors, sizes, genders, materials, quantities, and styles) */
  selection_list?: SelectionList | undefined;
}

export interface PriceRange {
  min?: number;
  max?: number;
}

export interface SelectionList {
  names?: string[];
}

export interface TapAction {
  action_url?: string;
}
